import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PencilIcon, CalculatorIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import PlaceholderImage from './PlaceholderImage';

const MyPropertyCard = ({ prop, onDelete }) => {
    const navigate = useNavigate();
    const [propertyImage, setPropertyImage] = useState(null);
    const [imageError, setImageError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [analysisData, setAnalysisData] = useState(null);
    const [showAnalysis, setShowAnalysis] = useState(false);

    useEffect(() => {
        const fetchPropertyImage = async () => {
            try {
                const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${prop.id}/imagenes`, {
                    credentials: "include"
                });
                if (!res.ok) {
                    throw new Error('Error fetching property images');
                }
                const data = await res.json();
                if (data && data.length > 0) {
                    setPropertyImage(data[0].url_imagen);
                }
            } catch (err) {
                console.error("Error fetching property image:", err);
            }
        };

        fetchPropertyImage();
    }, [prop.id]);

    const handleEdit = (e) => {
        e.stopPropagation();
        navigate(`/propiedades/${prop.id}/editar`);
    };

    const handleAnalyze = (e) => {
        e.stopPropagation();
        navigate(`/propiedades/${prop.id}/analizar`);
    };

    const handleCalculateReforma = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/calcular-reforma/${prop.id}`, {
                method: "POST",
                credentials: "include"
            });
            if (!res.ok) throw new Error('Error calculating reforma');
            const data = await res.json();
            toast.success("Análisis de reforma completado");
            // You might want to show the reforma details in a modal or navigate to a details page
        } catch (err) {
            console.error("Error calculating reforma:", err);
            toast.error("Error al calcular la reforma");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(`/propiedades/${prop.id}/analizar`)}
        >
            <div className="relative h-48 w-full">
                {(propertyImage || prop.imagen_url) && !imageError ? (
                    <img
                        src={propertyImage || prop.imagen_url}
                        alt={prop.titulo || "Imagen de la propiedad"}
                        className="w-full h-full object-cover"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <PlaceholderImage />
                )}

                <div className="absolute top-2 right-2 flex space-x-2">
                    <button
                        onClick={handleEdit}
                        className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
                        title="Editar propiedad"
                    >
                        <PencilIcon className="h-5 w-5 text-gray-600" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(prop.id);
                        }}
                        className="p-2 bg-white rounded-full shadow-md hover:bg-red-100 transition-colors"
                        title="Eliminar propiedad"
                    >
                        <TrashIcon className="h-5 w-5 text-red-600" />
                    </button>
                </div>
            </div>
            <div className="p-4">
                {prop.titulo && (
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        {prop.titulo}
                    </h3>
                )}
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                    <div>
                        <span className="font-medium">Habitaciones:</span> {prop.habitaciones}
                    </div>
                    <div>
                        <span className="font-medium">Baños:</span> {prop.banos}
                    </div>
                    <div>
                        <span className="font-medium">Superficie:</span> {prop.superficie}m²
                    </div>
                    <div>
                        <span className="font-medium">Distrito:</span> {prop.distrito}
                    </div>
                    <div>
                        <span className="font-medium">Municipio:</span> {prop.municipio}
                    </div>
                    <div>
                        <span className="font-medium">Precio:</span> {prop.precio}€
                    </div>
                </div>

                {showAnalysis && analysisData && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-800 mb-2">Análisis de Precio</h4>
                        <div className="space-y-2">
                            <div>
                                <span className="font-medium">Precio por m²:</span>{" "}
                                {Math.round(prop.precio / prop.superficie)}€/m²
                            </div>
                            <div>
                                <span className="font-medium">Media en la zona:</span>{" "}
                                {Math.round(analysisData.precioMedioZona)}€/m²
                            </div>
                            <div>
                                <span className="font-medium">Diferencia:</span>{" "}
                                <span className={analysisData.diferenciaPorcentual > 0 ? "text-green-600" : "text-red-600"}>
                                    {analysisData.diferenciaPorcentual > 0 ? "+" : ""}
                                    {analysisData.diferenciaPorcentual.toFixed(1)}%
                                </span>
                            </div>
                        </div>
                        <button
                            onClick={handleCalculateReforma}
                            disabled={loading}
                            className="mt-4 w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                            {loading ? "Calculando..." : "Calcular Coste de Reforma"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyPropertyCard; 